import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Download, BookOpen, Users, Building2, Target, Calendar, CheckSquare, Mic, Sparkles, HelpCircle } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { toast } from 'sonner';

const HelpManual = () => {
  const contentRef = useRef(null);

  const handleDownloadPDF = async () => {
    if (!contentRef.current) return;
    
    toast.info('Generando PDF...');
    
    const element = contentRef.current;
    const opt = {
      margin: [10, 10, 10, 10],
      filename: 'Manual_CRM_TYPSA.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    try {
      await html2pdf().set(opt).from(element).save();
      toast.success('PDF descargado correctamente');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error al generar el PDF');
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 shrink-0">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">Manual de Usuario</h2>
        </div>
        <Button onClick={handleDownloadPDF} variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          Descargar PDF
        </Button>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1 pr-4">
        <div ref={contentRef} className="prose prose-sm max-w-none dark:prose-invert">
          
          {/* Introducción */}
          <section className="mb-8">
            <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
              <BookOpen className="w-6 h-6" />
              CRM TYPSA - Manual de Usuario
            </h1>
            <p className="text-muted-foreground">
              El CRM TYPSA es una aplicación de gestión de relaciones comerciales diseñada específicamente 
              para TYPSA Digital Solutions. Permite gestionar empresas, contactos, oportunidades de negocio, 
              actividades y tareas, con funcionalidades avanzadas de captura de información por voz y 
              procesamiento con Inteligencia Artificial.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-600 mb-1" />
                <p className="text-sm font-medium">Empresas</p>
                <p className="text-xs text-muted-foreground">TYPSA, Privadas, Públicas</p>
              </div>
              <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                <Users className="w-5 h-5 text-green-600 mb-1" />
                <p className="text-sm font-medium">Contactos</p>
                <p className="text-xs text-muted-foreground">Directorio y relaciones</p>
              </div>
              <div className="p-3 bg-purple-50 dark:bg-purple-950 rounded-lg">
                <Target className="w-5 h-5 text-purple-600 mb-1" />
                <p className="text-sm font-medium">Oportunidades</p>
                <p className="text-xs text-muted-foreground">Pipeline de ventas</p>
              </div>
              <div className="p-3 bg-amber-50 dark:bg-amber-950 rounded-lg">
                <Calendar className="w-5 h-5 text-amber-600 mb-1" />
                <p className="text-sm font-medium">Actividades</p>
                <p className="text-xs text-muted-foreground">Reuniones y llamadas</p>
              </div>
              <div className="p-3 bg-red-50 dark:bg-red-950 rounded-lg">
                <CheckSquare className="w-5 h-5 text-red-600 mb-1" />
                <p className="text-sm font-medium">Tareas</p>
                <p className="text-xs text-muted-foreground">Gestión de pendientes</p>
              </div>
              <div className="p-3 bg-cyan-50 dark:bg-cyan-950 rounded-lg">
                <Mic className="w-5 h-5 text-cyan-600 mb-1" />
                <p className="text-sm font-medium">Notas de Voz</p>
                <p className="text-xs text-muted-foreground">Captura con IA</p>
              </div>
            </div>
          </section>

          <Separator className="my-6" />

          {/* Acceso al Sistema */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm">1</span>
              Acceso al Sistema
            </h2>
            
            <h3 className="text-lg font-medium mt-4">Iniciar Sesión</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Accede a la URL del CRM</li>
              <li>Introduce tu <strong>correo electrónico</strong> (@typsa.es)</li>
              <li>Introduce tu <strong>contraseña</strong></li>
              <li>Haz clic en <strong>"Iniciar sesión"</strong></li>
            </ol>

            <h3 className="text-lg font-medium mt-4">Cambiar Contraseña</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Haz clic en tu nombre de usuario (esquina inferior izquierda)</li>
              <li>Selecciona <strong>"Cambiar contraseña"</strong></li>
              <li>Introduce la contraseña actual y la nueva</li>
              <li>Confirma los cambios</li>
            </ol>
          </section>

          <Separator className="my-6" />

          {/* Mi Espacio */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm">2</span>
              Mi Espacio
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Tu centro de trabajo personal con todas tus pendientes y notas.
            </p>

            <h3 className="text-lg font-medium">Notificaciones</h3>
            <p className="text-sm mb-2">Las tarjetas de resumen muestran:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><span className="text-red-600 font-medium">Tareas vencidas</span>: Tareas con fecha límite pasada</li>
              <li><span className="text-amber-600 font-medium">Sin documentar</span>: Actividades pasadas sin documentación</li>
              <li><span className="text-blue-600 font-medium">Notas pendientes</span>: Notas de voz sin procesar</li>
            </ul>
            <p className="text-sm mt-2 p-2 bg-muted rounded">
              <strong>Tip:</strong> Haz clic en cualquier tarjeta para filtrar la lista.
            </p>

            <h3 className="text-lg font-medium mt-4">Mis Notas</h3>
            <p className="text-sm">
              Gestiona tus notas de voz organizadas por fecha. Cada nota muestra su estado de procesamiento.
            </p>

            <h3 className="text-lg font-medium mt-4">Registro (Solo Admin)</h3>
            <p className="text-sm">
              Panel de auditoría con rankings de uso y historial de acciones del sistema.
            </p>
          </section>

          <Separator className="my-6" />

          {/* Empresas */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm">3</span>
              Empresas
            </h2>

            <h3 className="text-lg font-medium mt-4">Tipos de Empresa</h3>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded text-center">
                <p className="text-sm font-medium">TYPSA</p>
                <p className="text-xs">Unidades internas</p>
              </div>
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded text-center">
                <p className="text-sm font-medium">Privadas</p>
                <p className="text-xs">Sector privado</p>
              </div>
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded text-center">
                <p className="text-sm font-medium">Públicas</p>
                <p className="text-xs">Administraciones</p>
              </div>
            </div>

            <h3 className="text-lg font-medium mt-4">Unidades TYPSA</h3>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>División:</strong> Grandes áreas de negocio</li>
              <li><strong>Departamento:</strong> Áreas funcionales</li>
              <li><strong>Territorio:</strong> Áreas geográficas</li>
              <li><strong>Grupo:</strong> Equipos específicos</li>
            </ul>

            <h3 className="text-lg font-medium mt-4">Historial de Cuenta</h3>
            <p className="text-sm">
              Cada empresa tiene un historial donde puedes registrar información relevante, 
              grabar notas de voz y usar el botón <strong>"Mejorar redacción"</strong> para pulir textos con IA.
            </p>
          </section>

          <Separator className="my-6" />

          {/* Contactos */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm">4</span>
              Contactos
            </h2>
            
            <h3 className="text-lg font-medium mt-4">Crear Nuevo Contacto</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Haz clic en <strong>"Nuevo Contacto"</strong></li>
              <li>Completa nombre, email, teléfono y cargo</li>
              <li>Selecciona la empresa asociada</li>
              <li>Marca <strong>"Contacto principal"</strong> si aplica</li>
              <li>Guarda los cambios</li>
            </ol>

            <h3 className="text-lg font-medium mt-4">Historial de Relación</h3>
            <p className="text-sm">
              Similar al historial de cuenta, permite documentar la evolución de la relación con cada contacto.
            </p>
          </section>

          <Separator className="my-6" />

          {/* Oportunidades */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm">5</span>
              Oportunidades
            </h2>

            <h3 className="text-lg font-medium mt-4">Pipeline de Ventas</h3>
            <p className="text-sm mb-2">Las oportunidades pasan por las siguientes etapas:</p>
            <div className="flex flex-wrap gap-1 text-xs">
              <span className="px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded">Identificada</span>
              <span className="px-2 py-1 bg-blue-200 dark:bg-blue-700 rounded">Contactada</span>
              <span className="px-2 py-1 bg-amber-200 dark:bg-amber-700 rounded">Propuesta</span>
              <span className="px-2 py-1 bg-purple-200 dark:bg-purple-700 rounded">Presentada</span>
              <span className="px-2 py-1 bg-orange-200 dark:bg-orange-700 rounded">Negociación</span>
              <span className="px-2 py-1 bg-green-200 dark:bg-green-700 rounded">Ganada</span>
              <span className="px-2 py-1 bg-red-200 dark:bg-red-700 rounded">Perdida</span>
            </div>

            <h3 className="text-lg font-medium mt-4">Mover Oportunidades</h3>
            <p className="text-sm">
              Arrastra y suelta las tarjetas entre columnas o usa el menú contextual para cambiar de etapa.
            </p>
          </section>

          <Separator className="my-6" />

          {/* Actividades */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm">6</span>
              Actividades
            </h2>

            <h3 className="text-lg font-medium mt-4">Tipos de Actividad</h3>
            <div className="grid grid-cols-5 gap-2 mt-2 text-center text-xs">
              <div className="p-2 bg-muted rounded">
                <p className="font-medium">Reunión</p>
              </div>
              <div className="p-2 bg-muted rounded">
                <p className="font-medium">Llamada</p>
              </div>
              <div className="p-2 bg-muted rounded">
                <p className="font-medium">Email</p>
              </div>
              <div className="p-2 bg-muted rounded">
                <p className="font-medium">Evento</p>
              </div>
              <div className="p-2 bg-muted rounded">
                <p className="font-medium">Visita</p>
              </div>
            </div>

            <h3 className="text-lg font-medium mt-4">Vista de Columnas</h3>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>Pendiente de documentar:</strong> Actividades pasadas sin documentación</li>
              <li><strong>Pasadas:</strong> Actividades ya documentadas</li>
              <li><strong>Próximas:</strong> Actividades futuras programadas</li>
            </ul>

            <h3 className="text-lg font-medium mt-4">Documentar una Actividad</h3>
            <p className="text-sm mb-2"><strong>Opción 1 - Con voz:</strong></p>
            <ol className="list-decimal list-inside space-y-1 text-sm ml-4">
              <li>Haz clic en el icono de micrófono</li>
              <li>Graba tu resumen hablado</li>
              <li>La IA extraerá: puntos clave, decisiones, próximos pasos</li>
            </ol>
            
            <p className="text-sm mb-2 mt-3"><strong>Opción 2 - Manual:</strong></p>
            <ol className="list-decimal list-inside space-y-1 text-sm ml-4">
              <li>Haz clic en "Documentar"</li>
              <li>Completa los campos</li>
              <li>Usa <strong>"Analizar con IA"</strong> para auto-rellenar</li>
            </ol>
          </section>

          <Separator className="my-6" />

          {/* Tareas */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm">7</span>
              Tareas
            </h2>

            <h3 className="text-lg font-medium mt-4">Vistas Disponibles</h3>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>Kanban:</strong> Tablero con columnas por estado</li>
              <li><strong>Lista:</strong> Vista detallada con filtros</li>
              <li><strong>Calendario:</strong> Vista mensual de fechas límite</li>
            </ul>

            <h3 className="text-lg font-medium mt-4">Crear Nueva Tarea</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Haz clic en <strong>"Nueva Tarea"</strong></li>
              <li>Completa título y fecha límite</li>
              <li>Selecciona el <strong>responsable</strong> del desplegable</li>
              <li>Asocia a empresa/oportunidad si aplica</li>
              <li>Guarda la tarea</li>
            </ol>

            <h3 className="text-lg font-medium mt-4">Estados</h3>
            <div className="flex gap-2 text-xs">
              <span className="px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded">Open</span>
              <span className="px-2 py-1 bg-blue-200 dark:bg-blue-700 rounded">In Progress</span>
              <span className="px-2 py-1 bg-green-200 dark:bg-green-700 rounded">Completed</span>
            </div>
          </section>

          <Separator className="my-6" />

          {/* Notas de Voz */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm">8</span>
              Notas de Voz
            </h2>

            <h3 className="text-lg font-medium mt-4">Grabar una Nota</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>Haz clic en el <strong>botón de micrófono</strong> (esquina inferior derecha)</li>
              <li>Habla claramente describiendo la información</li>
              <li>Haz clic en <strong>"Detener"</strong> cuando termines</li>
              <li>La nota se procesa automáticamente</li>
            </ol>

            <h3 className="text-lg font-medium mt-4">Tipos de Clasificación</h3>
            <div className="space-y-2 mt-2">
              <div className="p-2 bg-muted rounded text-sm">
                <p className="font-medium">Documentar Actividad</p>
                <p className="text-xs text-muted-foreground">Resumen de reunión/llamada → Vincular a actividad</p>
              </div>
              <div className="p-2 bg-muted rounded text-sm">
                <p className="font-medium">Generar Tareas</p>
                <p className="text-xs text-muted-foreground">Lista de pendientes → Crear tareas automáticamente</p>
              </div>
              <div className="p-2 bg-muted rounded text-sm">
                <p className="font-medium">Historial de Cuenta</p>
                <p className="text-xs text-muted-foreground">Info sobre empresa → Añadir al historial</p>
              </div>
              <div className="p-2 bg-muted rounded text-sm">
                <p className="font-medium">Historial de Relación</p>
                <p className="text-xs text-muted-foreground">Info sobre contacto → Añadir al historial</p>
              </div>
            </div>
          </section>

          <Separator className="my-6" />

          {/* Funcionalidades IA */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm">9</span>
              <Sparkles className="w-5 h-5" />
              Funcionalidades de IA
            </h2>

            <div className="space-y-4 mt-4">
              <div className="p-3 border rounded-lg">
                <h4 className="font-medium">Transcripción Automática</h4>
                <p className="text-sm text-muted-foreground">
                  Todas las notas de voz se transcriben automáticamente usando OpenAI Whisper.
                </p>
              </div>
              
              <div className="p-3 border rounded-lg">
                <h4 className="font-medium">Clasificación Inteligente</h4>
                <p className="text-sm text-muted-foreground">
                  La IA analiza el contenido y sugiere la mejor clasificación para tu nota.
                </p>
              </div>
              
              <div className="p-3 border rounded-lg">
                <h4 className="font-medium">Extracción de Información</h4>
                <p className="text-sm text-muted-foreground">
                  Para reuniones, la IA extrae: puntos clave, decisiones, próximos pasos, riesgos y oportunidades.
                </p>
              </div>
              
              <div className="p-3 border rounded-lg">
                <h4 className="font-medium">Analizar con IA</h4>
                <p className="text-sm text-muted-foreground">
                  En el formulario de actividad, el botón "Analizar con IA" rellena automáticamente los campos estructurados.
                </p>
              </div>
              
              <div className="p-3 border rounded-lg">
                <h4 className="font-medium">Mejorar Redacción</h4>
                <p className="text-sm text-muted-foreground">
                  En historiales, el botón "Mejorar redacción" pule y mejora el texto escrito.
                </p>
              </div>
            </div>
          </section>

          <Separator className="my-6" />

          {/* FAQ */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-7 h-7 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm">10</span>
              <HelpCircle className="w-5 h-5" />
              Preguntas Frecuentes
            </h2>

            <div className="space-y-3 mt-4">
              <details className="p-3 border rounded-lg">
                <summary className="font-medium cursor-pointer">¿Por qué no veo algunas opciones?</summary>
                <p className="text-sm text-muted-foreground mt-2">
                  Algunas funciones están restringidas según tu rol: Viewer (solo ver), Editor (crear/editar), Admin (acceso completo).
                </p>
              </details>
              
              <details className="p-3 border rounded-lg">
                <summary className="font-medium cursor-pointer">¿Cómo sé si una actividad está documentada?</summary>
                <p className="text-sm text-muted-foreground mt-2">
                  Las documentadas muestran badge verde. Las pendientes muestran "Sin documentar" en amarillo.
                </p>
              </details>
              
              <details className="p-3 border rounded-lg">
                <summary className="font-medium cursor-pointer">¿Por qué el desplegable de unidades TYPSA está vacío?</summary>
                <p className="text-sm text-muted-foreground mt-2">
                  Primero selecciona tipo "TYPSA", luego la categoría (División, Departamento, etc.), y el desplegable se llenará.
                </p>
              </details>
              
              <details className="p-3 border rounded-lg">
                <summary className="font-medium cursor-pointer">¿Qué pasa si no proceso una nota de voz?</summary>
                <p className="text-sm text-muted-foreground mt-2">
                  Aparecerá en Mi Espacio como "Nota pendiente" hasta que la proceses.
                </p>
              </details>
              
              <details className="p-3 border rounded-lg">
                <summary className="font-medium cursor-pointer">¿Puedo cambiar el responsable de una tarea?</summary>
                <p className="text-sm text-muted-foreground mt-2">
                  Sí, edita la tarea y selecciona otro usuario del desplegable de responsables.
                </p>
              </details>
            </div>
          </section>

          {/* Footer */}
          <div className="text-center text-xs text-muted-foreground mt-8 pb-4">
            <p>CRM TYPSA - Manual de Usuario</p>
            <p>Versión 2.0 - Febrero 2026</p>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default HelpManual;
